class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    const applyButtons = this.querySelectorAll('[data-facets-apply]');
    if (applyButtons.length > 0) {
      // When an Apply button exists, we do not auto-submit on every input change.
      applyButtons.forEach((button) => {
        button.addEventListener('click', this.onSubmitHandler.bind(this));
      });

      // Prevent "scroll-to-top" caused by focus jumping when clicking filter inputs.
      // We manually toggle checkbox/radio state on mousedown, and suppress the default click.
      if (facetForm) {
        // Keep sort dynamic even in Apply mode.
        facetForm.addEventListener('change', (event) => {
          if (event.target && event.target.name === 'sort_by') {
            this.onSubmitHandler(event);
            return;
          }

          if (event.target?.type === 'checkbox') {
            this.onSubmitHandler(event);
          }
        });

        facetForm.addEventListener(
          'mousedown',
          (event) => {
            // Only handle primary button (left click / tap).
            if (event.button !== 0) return;

            const inputEl = event.target.closest("input[type='checkbox'], input[type='radio']");
            const labelEl = event.target.closest('label[for]');
            const forId = labelEl ? labelEl.getAttribute('for') : null;
            const actualInput = inputEl || (forId ? document.getElementById(forId) : null);

            if (!actualInput || actualInput.disabled) return;

            // Don't interfere with Apply button clicks or active facet pill removal.
            if (event.target.closest('[data-facets-apply], facet-remove')) return;

            event.preventDefault();

            if (actualInput.type === 'checkbox') {
              actualInput.checked = !actualInput.checked;
              FacetFiltersForm.syncCheckboxVisualState(actualInput, actualInput.checked);
            } else if (actualInput.type === 'radio') {
              actualInput.checked = true;
            }

            actualInput.dataset.manualToggle = '1';
            actualInput.dispatchEvent(new Event('change', { bubbles: true }));
            actualInput.dispatchEvent(new Event('input', { bubbles: true }));
          },
          true
        );

        facetForm.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('facet-remove')) return;

            const inputEl = event.target.closest("input[type='checkbox'], input[type='radio']");
            const labelEl = event.target.closest('label[for]');
            const forId = labelEl ? labelEl.getAttribute('for') : null;
            const actualInput = inputEl || (forId ? document.getElementById(forId) : null);
            if (!actualInput) return;
            if (actualInput.dataset.manualToggle === '1') {
              event.preventDefault();
              event.stopPropagation();
              actualInput.dataset.manualToggle = '0';
            }
          },
          true
        );
      }
    } else if (facetForm) {
      // Default behavior (no Apply button): submit as the user changes inputs.
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove, facet-remove a').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static getSearchParamsFromUrl(href) {
    const rawHref = typeof href === 'string' ? href : href?.getAttribute?.('href') || href?.href || '';

    try {
      const url = new URL(rawHref, window.location.href);
      return url.search.slice(1);
    } catch {
      const queryIndex = rawHref.indexOf('?');
      return queryIndex === -1 ? '' : rawHref.slice(queryIndex + 1);
    }
  }

  static mergeSortParams(searchParams) {
    const sortSelect =
      document.querySelector('#FacetSortForm [name="sort_by"]') ||
      document.querySelector('#SortByDesktop') ||
      document.querySelector('#FacetFiltersForm [name="sort_by"]') ||
      document.querySelector('#FacetSortDrawerForm [name="sort_by"]');

    if (!sortSelect?.value) return searchParams || '';

    const parts = (searchParams || '')
      .split('&')
      .filter((part) => part && !part.startsWith('sort_by='));

    parts.push(`sort_by=${encodeURIComponent(sortSelect.value)}`);
    return parts.join('&');
  }

  static normalizeFilterValue(value) {
    try {
      return decodeURIComponent(value.toString().replace(/\+/g, ' ')).toLowerCase();
    } catch {
      return value.toString().toLowerCase();
    }
  }

  static paramValueMatches(params, name, value) {
    const target = FacetFiltersForm.normalizeFilterValue(value);

    return params.getAll(name).some((paramValue) => {
      return FacetFiltersForm.normalizeFilterValue(paramValue) === target;
    });
  }

  static syncCheckboxVisualState(input, isChecked) {
    input.checked = isChecked;

    const label =
      input.closest('label') ||
      (input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);

    if (label) {
      label.classList.toggle('active', isChecked);
    }
  }

  static getActiveFiltersFromSearchParams(searchParams) {
    const activeFilters = new Map();

    if (!searchParams) return activeFilters;

    searchParams.split('&').forEach((part) => {
      if (!part) return;

      const { name, value } = FacetFiltersForm.decodeParamPart(part);
      if (!name.startsWith('filter.')) return;

      if (!activeFilters.has(name)) activeFilters.set(name, new Set());
      activeFilters.get(name).add(FacetFiltersForm.normalizeFilterValue(value));
    });

    return activeFilters;
  }

  static syncFormsFromSearchParams(searchParams) {
    const activeFilters = FacetFiltersForm.getActiveFiltersFromSearchParams(searchParams);

    ['FacetFiltersForm', 'FacetFiltersFormMobile'].forEach((formId) => {
      const form = document.getElementById(formId);
      if (!form) return;

      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const activeValues = activeFilters.get(input.name);
        const shouldBeChecked =
          activeValues?.has(FacetFiltersForm.normalizeFilterValue(input.value)) ?? false;
        FacetFiltersForm.syncCheckboxVisualState(input, shouldBeChecked);
      });
    });
  }

  static uncheckFilterByNameAndValue(name, value) {
    const normalizedValue = FacetFiltersForm.normalizeFilterValue(value);

    document
      .querySelectorAll('#FacetFiltersForm input[type="checkbox"], #FacetFiltersFormMobile input[type="checkbox"]')
      .forEach((input) => {
        if (input.name === name && FacetFiltersForm.normalizeFilterValue(input.value) === normalizedValue) {
          FacetFiltersForm.syncCheckboxVisualState(input, false);
        }
      });
  }

  static clearPriceFilterInputs() {
    document
      .querySelectorAll('.price-range__input--min, .price-range__input--max')
      .forEach((input) => {
        input.value = '';
      });
  }

  static clearAllFilterInputs() {
    document
      .querySelectorAll('#FacetFiltersForm input[type="checkbox"], #FacetFiltersFormMobile input[type="checkbox"]')
      .forEach((input) => {
        FacetFiltersForm.syncCheckboxVisualState(input, false);
      });

    FacetFiltersForm.clearPriceFilterInputs();
  }

  static uncheckSingleFilterFromPill(link) {
    const paramName = link.dataset.facetParam;
    const paramValue = link.dataset.facetValue;

    if (paramName && paramValue != null && String(paramValue).length > 0) {
      FacetFiltersForm.uncheckFilterByNameAndValue(paramName, paramValue);
      return;
    }

    if (link.dataset.facetType === 'price') {
      FacetFiltersForm.clearPriceFilterInputs();
      return;
    }

    const pillInner = link.querySelector('.active-facets__button-inner');
    let pillText = '';

    if (pillInner) {
      for (const node of pillInner.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          pillText = node.textContent.trim();
          break;
        }
      }
    }

    if (!pillText) {
      FacetFiltersForm.clearPriceFilterInputs();
      return;
    }

    let matched = false;

    document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]').forEach((input) => {
      const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const labelText = label?.querySelector('.facet-checkbox__text-label')?.textContent?.trim();

      if (labelText && labelText.toLowerCase() === pillText.toLowerCase()) {
        FacetFiltersForm.uncheckFilterByNameAndValue(input.name, input.value);
        matched = true;
      }
    });

    if (!matched) {
      FacetFiltersForm.clearPriceFilterInputs();
    }
  }

  static countFilterValues(params) {
    let count = 0;

    for (const name of new Set([...params.keys()])) {
      if (name.startsWith('filter.')) {
        count += params.getAll(name).length;
      }
    }

    return count;
  }

  static buildSearchParamsFromForms() {
    const parts = [];
    const filterForm = document.getElementById('FacetFiltersForm');

    if (filterForm) {
      filterForm.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)').forEach((input) => {
        if (input.name) parts.push(`${input.name}=${input.value}`);
      });

      filterForm.querySelectorAll('.price-range__input--min, .price-range__input--max').forEach((input) => {
        if (input.name && input.value !== '') parts.push(`${input.name}=${input.value}`);
      });
    }

    return FacetFiltersForm.mergeSortParams(parts.join('&'));
  }

  static decodeParamPart(part) {
    const eqIndex = part.indexOf('=');
    if (eqIndex === -1) return { name: part, value: '' };

    const name = decodeURIComponent(part.slice(0, eqIndex).replace(/\+/g, ' '));
    const value = decodeURIComponent(part.slice(eqIndex + 1).replace(/\+/g, ' '));

    return { name, value };
  }

  static removeFilterParamFromString(searchParamsString, paramName, paramValue) {
    if (!searchParamsString) return '';

    const targetValue = FacetFiltersForm.normalizeFilterValue(paramValue);

    return searchParamsString
      .split('&')
      .filter((part) => {
        if (!part) return false;

        const { name, value } = FacetFiltersForm.decodeParamPart(part);

        if (name === paramName && FacetFiltersForm.normalizeFilterValue(value) === targetValue) {
          return false;
        }

        return true;
      })
      .join('&');
  }

  static getSearchParamsAfterRemovingFilter(link) {
    const paramName = link.dataset.facetParam;
    const paramValue = link.dataset.facetValue;
    const sourceParams = FacetFiltersForm.searchParamsPrev || window.location.search.slice(1);

    if (paramName && paramValue != null && String(paramValue).length > 0) {
      const updated = FacetFiltersForm.removeFilterParamFromString(sourceParams, paramName, paramValue);
      return FacetFiltersForm.mergeSortParams(updated);
    }

    return FacetFiltersForm.mergeSortParams(FacetFiltersForm.getSearchParamsFromUrl(link));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const isSortByEvent =
      Boolean(
        event &&
          event.target &&
          (event.target.name === 'sort_by' ||
            event.target.id === 'SortBy' ||
            event.target.id === 'SortBy-mobile' ||
            event.target.closest?.('#SortBy') ||
            event.target.closest?.('#SortBy-mobile'))
      ) || false;
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    if (!isSortByEvent) {
      loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
      document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
      if (countContainer) {
        countContainer.classList.add('loading');
      }
      if (countContainerDesktop) {
        countContainerDesktop.classList.add('loading');
      }
    } else {
      // Ensure any prior state doesn't leave the overlay visible.
      document.getElementById('ProductGridContainer').querySelector('.collection')?.classList.remove('loading');
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      if (countContainer) countContainer.classList.remove('loading');
      if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    if (Number.isFinite(FacetFiltersForm.pendingScrollTop)) {
      window.scrollTo(0, FacetFiltersForm.pendingScrollTop);
      FacetFiltersForm.pendingScrollTop = null;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const isFacetRemoveEvent = Boolean(event?.target?.closest?.('facet-remove'));
    const matchesId = (element) => {
      if (isFacetRemoveEvent) return false;
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        if (isFacetRemoveEvent) {
          currentElement.outerHTML = elementToRender.outerHTML;
        } else {
          currentElement.innerHTML = elementToRender.innerHTML;
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    FacetFiltersForm.syncFormsFromSearchParams(FacetFiltersForm.searchParamsPrev);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        // When the user clicks our desktop/mobile "Apply" button, avoid
        // forcing focus on the filter summary because focus can trigger
        // the browser to scroll the page (causing the jump to the top).
        const isApplyClick = Boolean(event.target.closest?.('[data-facets-apply]'));
        if (newElementToActivate && !isTextInput && !isApplyClick) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];
    const facetsContainer = document.querySelector('.facets-container');

    activeFacetElementSelectors.forEach((selector) => {
      const fetchedElements = html.querySelectorAll(selector);
      const domElements = facetsContainer
        ? facetsContainer.querySelectorAll(selector)
        : document.querySelectorAll(selector);

      domElements.forEach((domElement, index) => {
        if (fetchedElements[index]) {
          domElement.innerHTML = fetchedElements[index].innerHTML;
        }
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.pendingScrollTop = window.scrollY;
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const srcElement = event.srcElement || event.target;

    if (srcElement?.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
      return;
    }

    const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

    if (!isMobile) {
      this.onSubmitForm(FacetFiltersForm.buildSearchParamsFromForms(), event);
      return;
    }

    this.onSubmitForm(this.createSearchParams(event.target.closest('form')), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const link = event.currentTarget.closest('a') || event.currentTarget;
    const isClearAll = link.classList.contains('active-facets__button-remove');

    if (isClearAll) {
      FacetFiltersForm.clearAllFilterInputs();
      console.log('Clear form');
    } else {
      FacetFiltersForm.uncheckSingleFilterFromPill(link);
    }

    const searchParams = FacetFiltersForm.buildSearchParamsFromForms();
    FacetFiltersForm.filterData = [];
    FacetFiltersForm.renderPage(searchParams, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.pendingScrollTop = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.slider = this.querySelector('.price-range__slider');

    if (this.slider) {
      if (!this.slider.dataset.initialized) {
        this.slider.dataset.initialized = 'true';
        this.initRangeSlider();
      }
      return;
    }

    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  disconnectedCallback() {
    const slider = this.querySelector('.price-range__slider');
    if (slider) delete slider.dataset.initialized;
  }

  initRangeSlider() {
    this.minRange = this.querySelector('.price-range__range--min');
    this.maxRange = this.querySelector('.price-range__range--max');
    this.minInput = this.querySelector('.price-range__input--min');
    this.maxInput = this.querySelector('.price-range__input--max');
    this.minLabel = this.querySelector('.price-range__value--min');
    this.maxLabel = this.querySelector('.price-range__value--max');
    this.track = this.querySelector('.price-range__track');
    this.trackFill = this.querySelector('.price-range__track-fill');
    this.currencySymbol = this.querySelector('.price-range')?.dataset.currency || '£';
    // Use the attribute value (string) to avoid browser inconsistencies where
    // `inputElement.max` might be empty/non-numeric.
    this.rangeMax = Number.parseFloat(this.maxRange?.getAttribute('max')) || Number.parseFloat(this.maxRange?.max) || 0;

    this.onSliderInput = this.onSliderInput.bind(this);
    this.minRange.addEventListener('input', this.onSliderInput);
    this.maxRange.addEventListener('input', this.onSliderInput);

    this.updateZIndex(this.maxRange);
    this.syncHiddenInputs(Number(this.minRange.value), Number(this.maxRange.value));
    this.updateSlider();
  }

  onSliderInput(event) {
    let minVal = Number(this.minRange.value);
    let maxVal = Number(this.maxRange.value);

    if (minVal > maxVal) {
      if (event.currentTarget === this.minRange) {
        maxVal = minVal;
        this.maxRange.value = maxVal;
      } else {
        minVal = maxVal;
        this.minRange.value = minVal;
      }
    }

    this.updateZIndex(event.currentTarget);
    this.syncHiddenInputs(minVal, maxVal);
    this.updateSlider();
    this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updateZIndex(activeRange) {
    const minVal = Number(this.minRange.value);
    const maxVal = Number(this.maxRange.value);
    const gap = this.rangeMax * 0.05;

    if (maxVal - minVal <= gap) {
      if (activeRange === this.minRange) {
        this.minRange.style.zIndex = 5;
        this.maxRange.style.zIndex = 4;
      } else {
        this.maxRange.style.zIndex = 5;
        this.minRange.style.zIndex = 4;
      }
    } else if (minVal > this.rangeMax / 2) {
      this.minRange.style.zIndex = 5;
      this.maxRange.style.zIndex = 4;
    } else {
      this.maxRange.style.zIndex = 5;
      this.minRange.style.zIndex = 4;
    }
  }

  syncHiddenInputs(minVal, maxVal) {
    this.minInput.value = minVal > 0 ? this.formatInputValue(minVal) : '';
    this.maxInput.value = maxVal < this.rangeMax ? this.formatInputValue(maxVal) : '';

    if (this.maxInput.value) {
      this.minInput.setAttribute('data-max', this.maxInput.value);
    } else {
      this.minInput.setAttribute('data-max', this.rangeMax);
    }

    if (this.minInput.value) {
      this.maxInput.setAttribute('data-min', this.minInput.value);
    } else {
      this.maxInput.setAttribute('data-min', 0);
    }
  }

  formatInputValue(value) {
    const amount = Number(value);
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  }

  formatPrice(value) {
    const amount = Number(value);
    return `${this.currencySymbol}${amount.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  updateSlider() {
    const minVal = Number(this.minRange.value);
    const maxVal = Number(this.maxRange.value);
    const safeRangeMax =
      Number.isFinite(this.rangeMax) && this.rangeMax > 0
        ? this.rangeMax
        : Number.parseFloat(this.maxRange?.getAttribute('max')) || Number.parseFloat(this.maxRange?.max) || 0;
    if (!safeRangeMax) return;

    const percentMin = (minVal / safeRangeMax) * 100;
    const percentMax = (maxVal / safeRangeMax) * 100;
    const fillWidth = Math.max(0, percentMax - percentMin);

    if (this.track) {
      this.track.style.setProperty('--price-range-min', `${percentMin}%`);
      this.track.style.setProperty('--price-range-max', `${percentMax}%`);
    }

    if (this.trackFill) {
      this.trackFill.style.left = `${percentMin}%`;
      this.trackFill.style.width = `${fillWidth}%`;
    }

    this.minLabel.textContent = this.formatPrice(minVal);
    this.maxLabel.textContent = this.formatPrice(maxVal);
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input[type="text"], input:not([type])');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    event.stopPropagation();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


const closeButton = document.querySelector('.mobile-facets__close');
closeButton.addEventListener('click', function (){
  const mobileFaceForm = document.querySelector('.mobile-facets__disclosure');
  mobileFaceForm.classList.remove('menu-opening');
  document.body.classList.remove('overflow-hidden-mobile');
  mobileFaceForm.removeAttribute('open');
})